import React, { useState } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { contentApi } from '@/services/contentApi';

interface ContentItem {
  id: string;
  name: string;
  content_type: string;
  folder_path?: string;
}

interface CloneDialogProps {
  item: ContentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloned?: (newItem: ContentItem) => void;
}

export function CloneDialog({ item, open, onOpenChange, onCloned }: CloneDialogProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Reset name when dialog opens with new item
  React.useEffect(() => {
    if (open && item) {
      setName(`${item.name}_copy`);
    }
  }, [open, item]);

  const handleClone = async () => {
    if (!item) return;

    setLoading(true);
    try {
      const clonedItem = await contentApi.cloneItem(item.id, {
        name: name.trim() || undefined,
        folderPath: item.folder_path,
      });

      toast({
        title: 'Cloned',
        description: `Created "${clonedItem.name}"`,
      });

      onOpenChange(false);
      if (onCloned) {
        onCloned(clonedItem);
      }
    } catch (error) {
      console.error('Failed to clone item:', error);
      toast({
        title: 'Clone Failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && name.trim()) {
      handleClone();
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Clone "{item.name}"
          </DialogTitle>
          <DialogDescription>
            Create a copy of this {item.content_type} with a new name.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="clone-name">New Name</Label>
            <Input
              id="clone-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`${item.name}_copy`}
              disabled={loading}
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleClone} disabled={loading || !name.trim()}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cloning...
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Clone
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CloneDialog;
